/* =====================================================================
   Alex & Lace — Lenis smooth-scroll (LOCKED: ON, desktop).

   ON for desktop ≥1024 with a FINE pointer (mouse/trackpad).
   OFF for: mobile / coarse pointer (native touch scroll), reduced-motion, JS-off.
   (The ?smooth preview override was removed at defaults-lock 2026-07-17.)

   Config = escape.cafe's shipped Lenis (teardown §5): bare defaults — lerp 0.1,
   default easing (1.001 - 2^-10t), smoothWheel:true, smoothTouch:false.

   Lenis is loaded DYNAMICALLY only when it will actually run, so the off path
   (mobile/RM/coarse) costs ~nothing beyond this gate. When on, it integrates with
   GSAP ScrollTrigger (drives lenis.raf from the gsap ticker + updates ScrollTrigger
   on scroll) so the parallax / pin / reveals stay in sync with the lerped scroll.
   Body-lock: Horizon locks the body (overflow-hidden / .overflow-hidden) while the
   drawer, predictive search, or cart overlay is open. We watch <body> and stop()
   Lenis while locked, start() when released (escape.cafe's pattern) — so those
   overlays and their inner scroll behave natively. Anchor links + keyboard scroll
   keep working (Lenis only lerps window wheel/keys, doesn't capture focus).
   ===================================================================== */

(() => {
  // Capability gate (LOCKED): desktop, fine pointer, no reduced-motion.
  if (matchMedia('(prefers-reduced-motion: reduce)').matches) return; // no smoothing under reduced motion
  if (matchMedia('(max-width: 1023px)').matches) return; // native scroll on small screens
  if (matchMedia('(pointer: coarse)').matches) return;   // touch devices → native scroll

  const src = window.AL_LENIS_SRC;
  if (!src) return;

  const boot = () => {
    const Lenis = window.Lenis;
    const gsap = window.gsap;
    if (!Lenis) return;

    // Minimal Lenis CSS (injected only when smooth is on).
    const st = document.createElement('style');
    st.textContent =
      'html.lenis,html.lenis body{height:auto}' +
      '.lenis.lenis-smooth{scroll-behavior:auto !important}' +
      '.lenis.lenis-smooth [data-lenis-prevent]{overscroll-behavior:contain}' +
      '.lenis.lenis-stopped{overflow:hidden}';
    document.head.appendChild(st);

    // escape.cafe's shipped config (teardown §5): bare Lenis defaults — lerp 0.1, default easing,
    // smoothWheel:true, smoothTouch:false. (We already gate touch out above.)
    const lenis = new Lenis();

    if (gsap && window.ScrollTrigger) {
      lenis.on('scroll', window.ScrollTrigger.update);
      gsap.ticker.add((time) => lenis.raf(time * 1000));
      gsap.ticker.lagSmoothing(0);
      // Lenis just changed scroll metrics (html.lenis body{height:auto}) AFTER al-hero-motion
      // created its triggers — recompute their bounds and signal al-hero-motion to do the same,
      // so the footer dock / CTA pin don't run on stale start/end (see al-hero-motion refreshST).
      window.ScrollTrigger.refresh();
      window.dispatchEvent(new Event('al:lenis-ready'));
    } else {
      const raf = (t) => { lenis.raf(t); requestAnimationFrame(raf); };
      requestAnimationFrame(raf);
    }
    window.__alLenis = lenis; // handle for debugging

    // Body-lock handling: stop Lenis while ANY overlay locks the page, start it when released — so the
    // overlay + its inner scroll behave natively. The ESTABLISHED lock (DialogComponent for cart +
    // predictive search, and al-overlay-lock.js for the menu drawer) pins the body with
    // `position:fixed; top:-scrollY` — so THAT is the signal to watch. (The old check only looked for
    // `overflow-hidden`, which Horizon never sets → the observer was a no-op and Lenis kept scrolling
    // the background behind the drawer. This is the fix.) Keep the overflow checks too, harmlessly.
    const isLocked = () =>
      document.body.style.position === 'fixed' ||
      document.body.classList.contains('overflow-hidden') ||
      document.body.style.overflow === 'hidden' ||
      document.documentElement.classList.contains('overflow-hidden');
    let locked = isLocked();
    if (locked) lenis.stop();
    const lockObserver = new MutationObserver(() => {
      const nowLocked = isLocked();
      if (nowLocked === locked) return;
      locked = nowLocked;
      if (locked) lenis.stop(); else lenis.start();
    });
    lockObserver.observe(document.body, { attributes: true, attributeFilter: ['class', 'style'] });
    lockObserver.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
  };

  const s = document.createElement('script');
  s.src = src;
  s.onload = boot;
  document.head.appendChild(s);
})();
